/**
 * The random identifiers this library hands out, in one place.
 *
 * Lowercase hex of a fixed length, because that is the shape the things they
 * sit beside already use: a W3C trace id is 32 hex characters and a span id is
 * 16, and a `callId` matches the first so the two read alike in one log line
 * (`ER63`).
 *
 * The generator is the platform's rather than `Math.random`, for the reason
 * `randomFraction` in `@ns/resilience` gives: this package's lint gate has no
 * exemptions, and the alternative to picking the generator that answers the
 * question is a suppression comment nobody revisits. Both runtimes this library
 * targets have had `getRandomValues` for years.
 */

/** The hex digits of one byte, `'00'` through `'ff'`, built once. */
const HEX = Array.from({ length: 256 }, (_, byte) =>
  byte.toString(16).padStart(2, '0'),
);

/**
 * `bytes * 2` lowercase hex characters, drawn from the platform generator.
 *
 * A lookup table rather than `toString(16)` per byte, because the padding is
 * the part that gets forgotten: a byte below 16 renders as one character and
 * the id then comes out short, which no length check catches unless you wrote
 * one. Here the shape is a property of the table.
 */
export function randomHex(bytes: number): string {
  const draw = new Uint8Array(bytes);
  crypto.getRandomValues(draw);
  let hex = '';
  // `noUncheckedIndexedAccess` cannot see that a `Uint8Array` of this length
  // has these elements, and every byte is a valid index into a 256-entry table,
  // so neither fallback is reachable.
  for (const byte of draw) hex += HEX[byte] ?? '00';
  return hex;
}

/** One caller's attempt: 32 lowercase hex characters (`ER63`). */
export function newCallId(): string {
  return randomHex(16);
}
